/**
 * Hierarchy Service
 * 
 * Handles all hierarchy-related API calls (Cycles and Processes)
 */

import { apiRequest } from './apiClient';
import { EmptyResponse, FileUpload, RiskRanking, SortDescriptor, SyncfusionSearchParam } from './types';
import { parseCommaSeparated } from '../utils/stringUtils';

// ==================== Search Cycles/Processes for Selection ====================

export interface CycleItem {
  cycleId: string;
  cycleCode: string;
  cycleName: string;
}

export interface ProcessItem {
  processId: string;
  processCode: string;
  processName: string;
  riskAssessmentId: string;
  processOwnerId: string;
  processOwnerName: string;
}

export interface SearchCyclesRequest {
  annualReportId: string;
  searchTerm: string;
}

export interface SearchCyclesResponse {
  data: CycleItem[];
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface SearchProcessesRequest {
  annualReportId: string;
  searchTerm: string;
  cycleIds: string[];
}

export interface SearchProcessesResponse {
  data: ProcessItem[];
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Search cycles for selection (used in Financial Statements, Control Activities, Gaps)
 */
export const searchCycles = async (
  request: SearchCyclesRequest
): Promise<CycleItem[]> => {
  try {
    const response = await apiRequest<SearchCyclesResponse, SearchCyclesRequest>(
      'POST',
      '/financial-statements/cycles/list',
      request,
      true
    );
    return response.data;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to search cycles',
    };
  }
};

/**
 * Search processes for selection (used in Financial Statements, Control Activities, Gaps)
 */
export const searchProcesses = async (
  request: SearchProcessesRequest
): Promise<ProcessItem[]> => {
  try {
    const response = await apiRequest<SearchProcessesResponse, SearchProcessesRequest>(
      'POST',
      '/financial-statements/processes/list',
      request,
      true
    );
    return response.data;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to search processes',
    };
  }
};

/**
 * Cycle API
 */
export interface CycleBackend {
  Id: string;
  CycleCode: string;
  CycleName: string;
  Cycle: string;
  CreatedAt: string;
  CycleWeight: number;
}

export interface GetCyclesRequest {
  requiresCounts: boolean;
  skip: number;
  take: number;
  sorted?: SortDescriptor[];
  search?: SyncfusionSearchParam[];
}

export interface GetCyclesResponse {
  result: CycleBackend[];
  count: number;
}

export const getCycles = async (
  annualReportId: string,
  request: GetCyclesRequest
): Promise<GetCyclesResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.sorted && { sorted: request.sorted }),
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetCyclesResponse, typeof requestBody>(
      'POST',
      `/hierarchy/cycle/index-grid/${annualReportId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch cycles',
    };
  }
};

export interface CreateCycleRequest {
  annualReportId: string;
  cycleName: string;
  cycleWeight: number;
}

export const createCycle = async (
  request: CreateCycleRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, CreateCycleRequest>(
      'POST',
      '/hierarchy/cycle',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create cycle',
    };
  }
};

export interface UpdateCycleRequest {
  annualReportId: string;
  cycleId: string;
  cycleName: string;
  cycleWeight: number;
}

export const updateCycle = async (
  request: UpdateCycleRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateCycleRequest>(
      'PUT',
      '/hierarchy/cycle',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update cycle',
    };
  }
};

export const deleteCycle = async (
  cycleId: string
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, never>(
      'DELETE',
      `/hierarchy/cycle/${cycleId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete cycle',
    };
  }
};

/**
 * Process API
 */

// Process Owner types
export interface ProcessOwner {
  userId: string;
  userName: string;
  title: string; // Job title/position
}

// Backend response format
interface BackendProcessOwner {
  userId: string;
  name: string; // Backend uses 'name' instead of 'userName'
  title: string;
}

export interface SearchProcessOwnersRequest {
  companyId: string;
  searchTerm: string;
}

export interface SearchProcessOwnersResponse {
  data: BackendProcessOwner[];
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

export const searchProcessOwners = async (
  request: SearchProcessOwnersRequest
): Promise<ProcessOwner[]> => {
  try {
    const response = await apiRequest<SearchProcessOwnersResponse, SearchProcessOwnersRequest>(
      'POST',
      '/hierarchy/process-owners',
      request,
      true // Authentication required
    );
    
    // Map backend response to frontend format
    return response.data.map((owner: BackendProcessOwner) => ({
      userId: owner.userId,
      userName: owner.name, // Map 'name' to 'userName'
      title: owner.title,
    }));
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to search process owners',
    };
  }
};

// Create Process API
export interface CreateProcessRequest {
  cycleId: string;
  processName: string;
  processWeight: number;
  processOwnerId?: string; // Optional
  index: number; // For positioning (Add above/below)
  documentIds?: string[]; // Optional - Flowcharts
  testSheetId?: string; // Optional - Test Sheet
}

export const createProcess = async (
  request: CreateProcessRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, CreateProcessRequest>(
      'POST',
      '/hierarchy/process',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create process',
    };
  }
};

// Create Process API
export interface UpdateProcessRequest {
  cycleId: string;
  processId: string;
  processName: string;
  processWeight: number;
  processOwnerId?: string; // Optional
  documentIds?: string[]; // Optional - Flowcharts
  testSheetId?: string; // Optional - Test Sheet
}

export const updateProcess = async (
  request: UpdateProcessRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateProcessRequest>(
      'PUT',
      '/hierarchy/process',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update process',
    };
  }
};

export const deleteProcess = async (
  processId: string
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, never>(
      'DELETE',
      `/hierarchy/process/${processId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete process',
    };
  }
};

export interface ProcessBackend {
  Id: string;
  ProcessCode: string;
  Process: string; // Full process name "A01 Process 1"
  ProcessOwnerId: string;
  ProcessOwner: string;
  ProcessWeight: number;
  RiskRanking: RiskRanking;
  RiskAssessmentId: string;
  RiskRatingRaw: number;
  TotalControls: number;
  KeyControls: number;
  DownloadIds: string; // Comma-separated file IDs
  DownloadNames: string; // Comma-separated file names
  TestSheetId: string;
  TestSheetName: string;
  TestingStatus: number;
  TotalGaps: number;
  LowGaps: number;
  MidGaps: number;
  HighGaps: number;
  CreatedAt: string;
  InScopeControlCount: number;
}

export interface GetProcessesRequest {
  requiresCounts: boolean;
  skip: number;
  take: number;
  sorted?: SortDescriptor[];
  search?: SyncfusionSearchParam[];
}

export interface GetProcessesResponse {
  result: ProcessBackend[];
  count: number;
}

export const getProcesses = async (
  cycleId: string,
  request: GetProcessesRequest
): Promise<GetProcessesResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.sorted && { sorted: request.sorted }),
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetProcessesResponse, typeof requestBody>(
      'POST',
      `/hierarchy/process/index-grid/${cycleId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch processes',
    };
  }
};

/**
 * Frontend types
 */
export interface Cycle {
  id: string;
  cycleCode: string;
  cycleName: string;
  processCount: number;
  createdAt: string;
  updatedAt: string;
  cycleWeight: number;
}

export interface Process {
  id: string;
  processCode: string;
  processName: string;
  processWeight: number;
  processOwnerId: string;
  processOwner: string;
  riskRanking: RiskRanking;
  totalControls: number;
  keyControls: number;
  flowcharts: FileUpload[];
  testSheet?: FileUpload;
  testingStatus: number;
  gaps: { severity: 'High' | 'Med' | 'Low' | 'Unranked'; count: number }[];
  totalGaps: number;
  cycleId: string;
  riskAssessmentId: string;
  inScopeControlCount: number;
}

/**
 * Helper functions to parse backend data
 */

// Material color palette for cycles
export const CYCLE_COLORS = [
  '#E3F2FD', // Blue 50
  '#F3E5F5', // Purple 50
  '#E8F5E9', // Green 50
  '#FFF3E0', // Orange 50
  '#FCE4EC', // Pink 50
  '#E0F2F1', // Teal 50
  '#FFF9C4', // Yellow 50
  '#F1F8E9', // Light Green 50
  '#E1F5FE', // Light Blue 50
  '#FBE9E7', // Deep Orange 50
];

export const parseBackendCycles = (items: CycleBackend[]): Cycle[] => {
  // Backend handles sorting via 'sorted' parameter in request
  return items.map(parseBackendCycle);
};

export const parseBackendCycle = (item: CycleBackend): Cycle => {
  return {
    id: item.Id,
    cycleCode: item.CycleCode,
    cycleName: item.CycleName,
    processCount: 0, // Will be updated from processes data
    createdAt: item.CreatedAt,
    updatedAt: item.CreatedAt, // Use CreatedAt as UpdatedAt is not in response
    cycleWeight: item.CycleWeight,
  };
};

export const parseBackendProcesses = (items: ProcessBackend[], cycleId: string): Process[] => {
  // Backend handles sorting via 'sorted' parameter in request
  return items.map(item => parseBackendProcess(item, cycleId));
};  

export const parseBackendProcess = (item: ProcessBackend, cycleId: string): Process => {
  // Extract process name from "A01 Process 1" format
  const processNameMatch = item.Process?.match(/^[A-Z0-9.]+\s+(.+)$/);
  const processName = processNameMatch ? processNameMatch[1] : item.Process || '';

  // Parse flowcharts from DownloadIds and DownloadNames
  const downloadIds = parseCommaSeparated(item.DownloadIds || '');
  const downloadNames = parseCommaSeparated(item.DownloadNames || '');
  // console.log('downloadIds', downloadIds);
  // console.log('downloadNames', downloadNames);
  
  const flowcharts: FileUpload[] = downloadIds.map((id, index) => {
    return { id, name: downloadNames[index] || 'File' };
  });

  // Parse test sheets
  const testSheet: FileUpload | undefined = item.TestSheetId
    ? { id: item.TestSheetId, name: item.TestSheetName || 'Test Sheet' }
    : undefined;

  // Build gaps array from individual gap counts
  // Use RiskRanking type from gapService: 'High' | 'Med' | 'Low' | 'Unranked'
  const gaps: { severity: 'High' | 'Med' | 'Low' | 'Unranked'; count: number }[] = [];
  
  // Calculate total gaps and unranked gaps
  const totalGaps = item.TotalGaps || 0;
  const unrankedGaps = totalGaps - (item.LowGaps || 0) - (item.MidGaps || 0) - (item.HighGaps || 0);
  
  if (item.HighGaps > 0) {
    gaps.push({ severity: 'High', count: item.HighGaps });
  }
  if (item.MidGaps > 0) {
    gaps.push({ severity: 'Med', count: item.MidGaps });
  }
  if (item.LowGaps > 0) {
    gaps.push({ severity: 'Low', count: item.LowGaps });
  }
  if (unrankedGaps > 0) {
    gaps.push({ severity: 'Unranked', count: unrankedGaps });
  }

  return {
    id: item.Id,
    processCode: item.ProcessCode,
    processName: processName,
    processWeight: item.ProcessWeight || 0,
    processOwnerId: item.ProcessOwnerId || '',
    processOwner: item.ProcessOwner || '',
    riskRanking: item.RiskRanking || 'Unranked' as RiskRanking,
    totalControls: item.TotalControls || 0,
    keyControls: item.KeyControls || 0,
    flowcharts,
    testSheet,
    testingStatus: item.TestingStatus || 0,
    gaps,
    totalGaps,
    cycleId: cycleId,
    riskAssessmentId: item.RiskAssessmentId || '',
    inScopeControlCount: item.InScopeControlCount || 0,
  };
};

